import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { ArrowLeft, Mail, Phone } from "lucide-react";
import { Footer, GoldDots, Logo } from "@/components/Brand";
import { SUPPORT_EMAIL } from "@/lib/content";

const BackBar = ({ title }) => {
  const nav = useNavigate();
  const loc = useLocation();
  const { isSignedIn } = useAuth();
  const entry = useRef(loc.state?.entryLen ?? window.history.length);
  const fromApp = !loc.state?.direct && loc.key !== "default";
  const inNewTab = !fromApp && window.opener;
  const label = inNewTab ? "Close" : fromApp ? "Back" : isSignedIn ? "Back to AHU" : "Back to sign in";
  const back = () => {
    if (inNewTab) return window.close();
    if (!fromApp) return nav(isSignedIn ? "/" : "/sign-in");
    window.history.go(-Math.max(1, window.history.length - entry.current + 1));
  };
  return (
    <header className="bg-charcoal sticky top-0 z-20">
      <div className="mx-auto max-w-5xl px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <button onClick={back} className="inline-flex items-center gap-2 rounded-full border border-alabaster/30 px-4 py-2 text-sm text-alabaster hover:border-gold"
          style={{ transition: "border-color .3s ease" }} data-testid="info-back-btn">
          <ArrowLeft size={16} /> {label}
        </button>
        <span className="font-display text-alabaster/90 truncate" data-testid="info-title">{title}</span>
        <Logo variant="gold" className="h-6 hidden sm:block" />
      </div>
    </header>
  );
};

const WebsitePage = ({ title, page, testId }) => {
  const nav = useNavigate();
  const loc = useLocation();
  const [html, setHtml] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setHtml(null);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/legal/${page}`).then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setHtml(d.html)).catch(() => setFailed(true));
  }, [page]);
  const onClick = (e) => {
    const a = e.target.closest("a");
    const href = a?.getAttribute("href");
    if (href?.startsWith("/")) { e.preventDefault(); nav(href, { replace: true, state: loc.state ?? (loc.key === "default" ? { direct: true } : undefined) }); }
  };
  return (
    <div className="min-h-[100dvh] flex flex-col bg-alabaster" data-testid={testId}>
      <BackBar title={title} />
      <main className="flex-1 w-full mx-auto max-w-3xl px-5 sm:px-8 py-10 sm:py-14">
        {failed ? (
          <p className="text-ink-soft" data-testid={`${testId}-error`}>This page couldn't load just now. You can read it at <a className="underline underline-offset-4" href={`https://ahucoach.com/${page}.html`} target="_blank" rel="noopener noreferrer">ahucoach.com</a>.</p>
        ) : !html ? <div className="py-16 flex justify-center"><GoldDots /></div> : (
          <article className="legal-prose ahu-card p-6 sm:p-10 fade-in" onClick={onClick} dangerouslySetInnerHTML={{ __html: html }} data-testid={`${testId}-content`} />
        )}
      </main>
      <Footer />
    </div>
  );
};

export const TermsPage = () => <WebsitePage title="Terms of Service" page="terms" testId="terms-page" />;
export const PrivacyPage = () => <WebsitePage title="Privacy Policy" page="privacy" testId="privacy-page" />;

const HelpItem = ({ q, children }) => (
  <div className="py-5 border-b border-rule last:border-0">
    <p className="font-display text-lg text-charcoal">{q}</p>
    <div className="mt-1.5 text-[15px] text-ink leading-relaxed">{children}</div>
  </div>
);

export const HelpPage = () => (
  <div className="min-h-[100dvh] flex flex-col bg-alabaster" data-testid="help-page">
    <BackBar title="Help" />
    <main className="flex-1 w-full mx-auto max-w-2xl px-5 sm:px-8 py-12 fade-in">
      <p className="eyebrow mb-3">Help and support</p>
      <h1 className="text-3xl sm:text-4xl">How can we help?</h1>
      <div className="ahu-card mt-8 p-6 sm:p-8">
        <HelpItem q="I can't sign in">Choose <strong>Forgot password?</strong> on the sign-in screen and enter your email. We'll send you a 6-digit code to set a new password. If you joined with Google, choose <strong>Continue with Google</strong>.</HelpItem>
        <HelpItem q="I used the first AHU app">Sign in with the same email and password you used before. Your plans have come across with you.</HelpItem>
        <HelpItem q="Membership and payments">Change or cancel any time from Settings, Membership. If you cancel, your access continues to the end of your paid period, and your plans are never deleted.</HelpItem>
        <HelpItem q="Talk to us">
          <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex items-center gap-2 underline underline-offset-4" data-testid="help-email-link"><Mail size={16} />{SUPPORT_EMAIL}</a>
        </HelpItem>
      </div>
      <div className="ahu-card mt-5 p-6 sm:p-8" data-testid="help-safety">
        <p className="font-display text-lg text-charcoal">If you're doing it really tough</p>
        <p className="mt-2 text-[15px] leading-relaxed">AHU isn't a crisis service. Free call or text <a href="tel:1737" className="underline underline-offset-4">1737</a> any time to talk with a trained counsellor. In an emergency, call <a href="tel:111" className="underline underline-offset-4">111</a>.</p>
        <p className="mt-2 text-[15px] leading-relaxed inline-flex items-center gap-2"><Phone size={15} /> Lifeline 0800 543 354 · Suicide Crisis Helpline 0508 828 865</p>
        <p className="mt-2 text-sm text-ink-soft">Outside New Zealand, visit <a href="https://findahelpline.com" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">findahelpline.com</a>.</p>
      </div>
    </main>
    <Footer />
  </div>
);
